import { z } from 'zod'
import type { ProductCategoryId } from '../constants'
import type { Market } from '../schemas/simulation'
import type { Axis } from '../taxonomy'

/**
 * 인증 준비도 가이드 — 판매·인증 목표 (2026-11-01, 설계 recipecat-cert-readiness/10_기능_설계.md 5-1).
 * 사용자가 만들기 전에 고르는 "어느 기준에 맞춰 설계할까" 이다. 앱은 인증 주체가 아니다:
 * 인증은 제조 공장이 인증기관에서 받고, 앱은 설계를 그 기준 쪽으로 유도한 뒤 남은 일을 "누가" 와 함께 보여 준다.
 *
 * - 목표 값 4개 중 1단계는 할랄·해썹만 연다 (CERT_TARGETS_AVAILABLE). FDA·중국은 2·3단계에서 연다.
 * - 값 id 는 한 번 정하면 바꾸지 않는다 (저장된 input·result 가 이 id 를 들고 있다).
 * - 선택 칸이라 비우면 지금과 같다. 옛 앱은 보내지 않고, 옛 서버는 모르는 칸이라 버린다.
 */
export const CERT_TARGETS = ['halal', 'haccp', 'fda', 'china'] as const
export type CertTarget = (typeof CERT_TARGETS)[number]

/** 지금 서버가 규칙·문구·검사를 갖춘 목표 (1단계). 여기 없는 목표는 요청에 와도 버린다 */
export const CERT_TARGETS_AVAILABLE: readonly CertTarget[] = ['halal', 'haccp']

/** 할랄 판매국. undecided = 아직 모름(기본). 나라마다 인정하는 인증기관이 달라서 묻는다 */
export const HALAL_MARKETS = ['undecided', 'kr', 'my', 'id', 'ae'] as const
export type HalalMarket = (typeof HALAL_MARKETS)[number]
export const DEFAULT_HALAL_MARKET: HalalMarket = 'undecided'

/** 규칙 판. 결과(certReadiness.version)에 남겨, 규칙이 바뀐 뒤에도 그때 어느 판으로 점검했는지 안다 */
export const CERT_RULES_VERSION = 'cert-2026-11-01'
/** 규칙을 1차 출처에서 다시 읽은 날 (rules.generated.ts 의 readDate 와 같다). 상세 시트 "규칙 기준일" */
export const CERT_RULES_READ_DATE = '2026-11-01'

export const certGoalsSchema = z.object({
  targets: z.array(z.enum(CERT_TARGETS)).max(4),
  /** 할랄을 고른 때만 의미가 있다. 없으면 아직 모름 */
  halalMarket: z.enum(HALAL_MARKETS).optional(),
})
export type CertGoals = z.infer<typeof certGoalsSchema>

/** 해썹은 한국 식품위생법 제도라 판매 시장이 한국일 때만 고를 수 있다 */
export function certTargetAllowed(target: CertTarget, market: Market): boolean {
  if (target === 'haccp') return market === 'kr'
  return true
}

/**
 * 이 분류의 제품에 고를 수 있는 목표인지. 주류는 제품 자체가 술이라 할랄 기준에 맞출 수 없다
 * (MUI Fatwa 10/2018: 에탄올 0.5% 이상 발효 음료는 khamr) — 고르기 칩에서도 감춘다
 */
export function certTargetAllowedForCategory(
  target: CertTarget,
  categoryId: ProductCategoryId | string | null | undefined,
): boolean {
  return !(target === 'halal' && categoryId === 'alcohol')
}

/**
 * 요청·저장값을 정리한다. 식품 축만, 열린 목표만, 시장·분류에 맞는 목표만 남기고 순서는 CERT_TARGETS 순서로 맞춘다.
 * 남는 목표가 없으면 null — 호출하는 쪽은 칸을 아예 두지 않는다 (목표 없는 요청의 input·프롬프트가 지금과 같게).
 * allowedTargets 는 관리자 스위치(settings.certReadiness.targets)로 더 좁힐 때 넘긴다.
 */
export function normalizeCertGoals(
  goals: unknown,
  options: {
    axis: Axis | null | undefined
    market: Market
    categoryId?: ProductCategoryId | string | null
    allowedTargets?: readonly CertTarget[]
  },
): CertGoals | null {
  if ((options.axis ?? 'product') !== 'product') return null
  const parsed = certGoalsSchema.safeParse(goals)
  if (!parsed.success) return null
  const allowed = options.allowedTargets ?? CERT_TARGETS_AVAILABLE
  const targets = CERT_TARGETS.filter(
    (t) =>
      parsed.data.targets.includes(t) &&
      CERT_TARGETS_AVAILABLE.includes(t) &&
      allowed.includes(t) &&
      certTargetAllowed(t, options.market) &&
      certTargetAllowedForCategory(t, options.categoryId),
  )
  if (targets.length === 0) return null
  return targets.includes('halal')
    ? { targets, halalMarket: parsed.data.halalMarket ?? DEFAULT_HALAL_MARKET }
    : { targets }
}

/** 저장된 input 에서 목표를 꺼낸다 (목표 전 행·다른 축은 null) */
export function certGoalsOf(
  input:
    | { axis?: Axis | null; categoryId?: ProductCategoryId | string | null; certGoals?: unknown }
    | null
    | undefined,
  market: Market,
  allowedTargets?: readonly CertTarget[],
): CertGoals | null {
  if (!input?.certGoals) return null
  return normalizeCertGoals(input.certGoals, {
    axis: input.axis,
    market,
    categoryId: input.categoryId,
    ...(allowedTargets ? { allowedTargets } : {}),
  })
}

/** 이 판매국에서 확인해야 하는지 (아직 모름은 모든 수출 판매국 규칙을 보여 준다) */
export function halalMarketIncludes(goal: HalalMarket | undefined, market: 'my' | 'id' | 'ae') {
  const m = goal ?? DEFAULT_HALAL_MARKET
  return m === market || m === 'undecided'
}
